/**
 * REDEX Tenant – Demo Data
 *
 * Digital-asset-focused offerings: Licenses, Mining, Funds, Debt, Infrastructure
 * Mirrors the shape exported by mockDataExtended.ts so DemoPortal can swap seamlessly.
 */

import { PropertyCardData } from '../components/new-components/PropertyCard';
import { CryptoAsset } from '../components/new-components/CryptoStaking.types';
import {
  FundMetrics,
  FundraisingProgress,
  PerformanceDataPoint,
  AllocationItem,
  FundHighlight,
} from '../components/new-components/HiveFund.types';

// ─────────────────────────────────────────────────────────────────────────────
// Invest-tab cards  (PropertyCardData shape – reuses existing card renderer)
// ─────────────────────────────────────────────────────────────────────────────

export const REDEX_DEMO_PROPERTIES: PropertyCardData[] = [
  // ── Licenses ───────────────────────────────────────────────────────────
  {
    id: 'redex-lic-001',
    name: 'Capital Infrastructure License',
    address: 'Founding-cohort allocation in the REDEX Capital Infrastructure Network — 50 licensed operator seats powering cross-chain settlement.',
    city: 'Digital',
    state: 'Network',
    imageUrl: 'https://images.unsplash.com/photo-1639762681057-408e52192e55?w=1200&auto=format&fit=crop&q=80',
    propertyType: 'license',
    price: 2500000,   // annual allocation pool
    totalShares: 50,
    availableShares: 18,
    targetReturn: 0,
    investorCount: 32,
    fundedPercentage: 64,
    status: 'available',
    tags: ['license-offer', 'license'],
  },
  {
    id: 'redex-lic-002',
    name: 'Oracle Validator License',
    address: 'Run a validated oracle node for the REDEX price-feed network — 30 seats available with quarterly revenue share.',
    city: 'Decentralized',
    state: 'Global',
    imageUrl: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1200&auto=format&fit=crop&q=80',
    propertyType: 'license',
    price: 1200000,
    totalShares: 30,
    availableShares: 12,
    targetReturn: 0,
    investorCount: 18,
    fundedPercentage: 60,
    status: 'available',
    tags: ['license-offer', 'license'],
  },

  // ── Mining ─────────────────────────────────────────────────────────────
  {
    id: 'redex-mine-001',
    name: 'Valley Node TAO Mining',
    address: 'Bittensor subnet mining operation — high-performance GPU cluster generating TAO token rewards.',
    city: 'Valley Node',
    state: 'TAO',
    imageUrl: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&auto=format&fit=crop&q=80',
    propertyType: 'mining',
    price: 750000,
    totalShares: 1500,
    availableShares: 1050,
    targetReturn: 24.5,
    investorCount: 87,
    fundedPercentage: 30,
    status: 'available',
    tags: ['mining', 'TAO'],
  },
  {
    id: 'redex-mine-002',
    name: 'GPU Compute Cluster',
    address: 'Enterprise-grade NVIDIA H100 compute cluster — AI inference & PoW mining revenue.',
    city: 'Dallas',
    state: 'TX',
    imageUrl: 'https://images.unsplash.com/photo-1597852074816-d933c7d2b988?w=1200&auto=format&fit=crop&q=80',
    propertyType: 'mining',
    price: 1500000,
    totalShares: 3000,
    availableShares: 900,
    targetReturn: 18.2,
    investorCount: 214,
    fundedPercentage: 70,
    status: 'available',
    tags: ['mining', 'GPU'],
  },

  // ── Funds ──────────────────────────────────────────────────────────────
  {
    id: 'redex-fund-001',
    name: 'REDEX Growth Fund',
    address: 'Diversified digital-asset growth vehicle — multi-strategy exposure across DeFi, infrastructure, and compute.',
    city: 'Multi-Strategy',
    state: 'Global',
    imageUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&auto=format&fit=crop&q=80',
    propertyType: 'fund',
    price: 10000000,
    totalShares: 100000,
    availableShares: 65000,
    targetReturn: 15.0,
    investorCount: 342,
    fundedPercentage: 35,
    status: 'available',
    tags: ['fund'],
  },
  {
    id: 'redex-fund-002',
    name: 'Stablecoin Yield Fund',
    address: 'Low-volatility yield generation via audited DeFi lending protocols and on-chain money-market positions.',
    city: 'Multi-Chain',
    state: 'DeFi',
    imageUrl: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&auto=format&fit=crop&q=80',
    propertyType: 'fund',
    price: 5000000,
    totalShares: 50000,
    availableShares: 22500,
    targetReturn: 8.5,
    investorCount: 189,
    fundedPercentage: 55,
    status: 'available',
    tags: ['fund', 'stablecoin'],
  },

  // ── Debt ───────────────────────────────────────────────────────────────
  {
    id: 'redex-debt-001',
    name: 'Bridge Note Series A',
    address: 'Short-term secured bridge facility for institutional crypto-collateralised lending — 12-month term, quarterly coupon.',
    city: 'Fixed Income',
    state: 'Secured',
    imageUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&auto=format&fit=crop&q=80',
    propertyType: 'debt',
    price: 2000000,
    totalShares: 2000,
    availableShares: 600,
    targetReturn: 11.0,
    investorCount: 156,
    fundedPercentage: 70,
    status: 'available',
    tags: ['debt', 'fixed-income'],
  },

  // ── Infrastructure / Development ───────────────────────────────────────
  {
    id: 'redex-infra-001',
    name: 'Cross-Chain Settlement Hub',
    address: 'Build-out of cross-chain atomic-swap settlement infrastructure connecting EVM, Cosmos, and Bittensor networks.',
    city: 'Multi-Chain',
    state: 'Protocol',
    imageUrl: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&auto=format&fit=crop&q=80',
    propertyType: 'development',
    price: 3000000,
    totalShares: 6000,
    availableShares: 4200,
    targetReturn: 20.0,
    investorCount: 64,
    fundedPercentage: 30,
    status: 'available',
    tags: ['development', 'infrastructure'],
  },
  {
    id: 'redex-infra-002',
    name: 'Renewable Energy Data Centre',
    address: 'Solar-powered edge data centre in West Texas — hosting mining, AI training, and validator workloads.',
    city: 'Pecos',
    state: 'TX',
    imageUrl: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200&auto=format&fit=crop&q=80',
    propertyType: 'development',
    price: 4500000,
    totalShares: 9000,
    availableShares: 5400,
    targetReturn: 16.8,
    investorCount: 112,
    fundedPercentage: 40,
    status: 'available',
    tags: ['development', 'energy'],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Portfolio Holdings (demo user owns positions in several offerings)
// ─────────────────────────────────────────────────────────────────────────────

export const REDEX_DEMO_PORTFOLIO_HOLDINGS = [
  {
    propertyId: 'redex-mine-001',
    propertyName: 'Valley Node TAO Mining',
    propertyImage: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&auto=format&fit=crop&q=60',
    sharesOwned: 200,
    currentValue: 108400,
    initialInvestment: 100000,
    returnPercentage: 8.4,
    lastDividend: 2040.00,
    dividendYield: 24.5,
    monthlyIncome: 2040.00,
    acquisitionDate: '2025-09-15',
  },
  {
    propertyId: 'redex-fund-001',
    propertyName: 'REDEX Growth Fund',
    propertyImage: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&auto=format&fit=crop&q=60',
    sharesOwned: 500,
    currentValue: 54250,
    initialInvestment: 50000,
    returnPercentage: 8.5,
    lastDividend: 625.00,
    dividendYield: 15.0,
    monthlyIncome: 625.00,
    acquisitionDate: '2025-06-01',
  },
  {
    propertyId: 'redex-mine-002',
    propertyName: 'GPU Compute Cluster',
    propertyImage: 'https://images.unsplash.com/photo-1597852074816-d933c7d2b988?w=400&auto=format&fit=crop&q=60',
    sharesOwned: 150,
    currentValue: 82350,
    initialInvestment: 75000,
    returnPercentage: 9.8,
    lastDividend: 1137.50,
    dividendYield: 18.2,
    monthlyIncome: 1137.50,
    acquisitionDate: '2025-08-10',
  },
  {
    propertyId: 'redex-debt-001',
    propertyName: 'Bridge Note Series A',
    propertyImage: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&auto=format&fit=crop&q=60',
    sharesOwned: 100,
    currentValue: 105500,
    initialInvestment: 100000,
    returnPercentage: 5.5,
    lastDividend: 916.67,
    dividendYield: 11.0,
    monthlyIncome: 916.67,
    acquisitionDate: '2025-11-01',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard Summary
// ─────────────────────────────────────────────────────────────────────────────

export const REDEX_DEMO_DASHBOARD_SUMMARY = {
  totalPortfolioValue: 350500,
  totalInvestment: 325000,
  totalReturn: 25500,
  totalReturnPercentage: 7.85,
  monthlyIncome: 4719.17,
  annualizedReturn: 17.4,
  activeProperties: 4,
  totalShares: 950,
  lifetimeDividends: 18875.00,
  projectedAnnualIncome: 56630,
};

// ─────────────────────────────────────────────────────────────────────────────
// Crypto Assets (TAO-forward)
// ─────────────────────────────────────────────────────────────────────────────

export const REDEX_DEMO_CRYPTO_ASSETS: CryptoAsset[] = [
  {
    symbol: 'TAO',
    name: 'Bittensor',
    balance: '48.2500',
    usdValue: 18336,
    staked: '30.0000',
    stakedUsd: 11400,
    apy: 0.185,
    available: '18.2500',
    availableUsd: 6936,
  },
  {
    symbol: 'ETH',
    name: 'Ethereum',
    balance: '5.4200',
    usdValue: 16260,
    staked: '3.0000',
    stakedUsd: 9000,
    apy: 0.048,
    available: '2.4200',
    availableUsd: 7260,
  },
  {
    symbol: 'USDC',
    name: 'USD Coin',
    balance: '25000.00',
    usdValue: 25000,
    staked: '15000.00',
    stakedUsd: 15000,
    apy: 0.042,
    available: '10000.00',
    availableUsd: 10000,
  },
];

export const REDEX_DEMO_CRYPTO_NETWORK_FEE = {
  amount: 0.85,
  isEstimate: true,
};

// ─────────────────────────────────────────────────────────────────────────────
// Hive Fund → REDEX Growth Fund equivalents
// ─────────────────────────────────────────────────────────────────────────────

export const REDEX_DEMO_FUND_METRICS: FundMetrics = {
  targetApy: 0.15,
  minInvestment: 10000,
  termYears: 3,
  investorCount: 342,
};

export const REDEX_DEMO_FUND_FUNDRAISING: FundraisingProgress = {
  raised: 3500000,
  target: 10000000,
  percentFunded: 35,
};

export const REDEX_DEMO_FUND_PERFORMANCE: PerformanceDataPoint[] = [
  { month: 'Sep', value: 10000 },
  { month: 'Oct', value: 10820 },
  { month: 'Nov', value: 11450 },
  { month: 'Dec', value: 12100 },
  { month: 'Jan', value: 12860 },
  { month: 'Feb', value: 13500 },
];

export const REDEX_DEMO_FUND_ASSET_ALLOCATION: AllocationItem[] = [
  { label: 'Mining & Compute', percentage: 35, color: 'hsl(var(--primary))' },
  { label: 'DeFi Yield', percentage: 25, color: '#1944FF' },
  { label: 'Infrastructure', percentage: 20, color: '#242424' },
  { label: 'Tokenised Debt', percentage: 12, color: '#888888' },
  { label: 'Liquid Tokens', percentage: 8, color: '#cc3333' },
];

export const REDEX_DEMO_FUND_GEO_DISTRIBUTION: AllocationItem[] = [
  { label: 'North America', percentage: 45 },
  { label: 'Europe', percentage: 25 },
  { label: 'Asia-Pacific', percentage: 20 },
  { label: 'Global / Decentralized', percentage: 10 },
];

export const REDEX_DEMO_FUND_HIGHLIGHTS: FundHighlight[] = [
  { text: 'Multi-strategy digital-asset exposure' },
  { text: 'Institutional-grade custody via Fireblocks' },
  { text: 'Monthly distributions in USDC or fiat' },
  { text: 'On-chain transparent reporting' },
];

export const REDEX_DEMO_FUND_DESCRIPTION = `The REDEX Growth Fund provides diversified exposure to high-conviction digital-asset strategies including Bittensor subnet mining, DeFi yield farming, GPU compute revenue, and tokenised fixed-income instruments. Professionally managed with monthly distributions and on-chain reporting.`;

export const REDEX_DEMO_FUND_DISCLAIMER = `The REDEX Growth Fund is a private digital-asset investment vehicle with a 3-year lock-up period. Minimum investment of $10,000 required. Digital asset investments involve significant risk, including possible loss of principal. Past performance does not guarantee future results. Please review the full offering memorandum before investing.`;
